import { useRef, useCallback } from 'react';
import { HStack, VStack, Text, Icon, IconButton, Tooltip, Box } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { ChevronRight, SquareArrowOutUpRight } from 'lucide-react';

export default function CardGroup({
  title,
  subTitle,
  cardType = 'default',
  children,
  openPanel,
  viewInListPath,
}: {
  title: string;
  subTitle?: string;
  cardType?: 'default' | 'muted';
  children: React.ReactNode;
  openPanel?: () => void;
  viewInListPath?: string;
}) {
  const childrenRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    ({ target }: React.MouseEvent<HTMLDivElement>) => {
      if (!(target instanceof Node)) {
        return;
      }
      if (childrenRef.current?.contains(target) === true) {
        return;
      }

      if (openPanel !== undefined) {
        openPanel();
      }
    },
    [openPanel],
  );

  return (
    <VStack
      borderRadius="2xl"
      pt="2"
      px="3"
      pb="3"
      gap="2"
      backgroundColor={cardType === 'muted' ? 'bg.subtle' : 'fg.muted'}
      flex="1"
      {...(handleClick !== undefined && {
        cursor: 'pointer',
        onClick: handleClick,
        transition: 'box-shadow 0.3s ease-in-out',
        _hover: {
          boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        },
      })}
    >
      <HStack w="full" gap="4" justifyContent="space-between">
        <HStack gap="1" alignItems="baseline" minW={0}>
          <Text
            textStyle="sm"
            fontWeight="semibold"
            lineHeight="6"
            noOfLines={1}
            color={cardType === 'muted' ? 'fg.muted' : 'fg.inverse'}
          >
            {title}
          </Text>
          {subTitle != null ? (
            <Text
              fontWeight="normal"
              fontSize="xs"
              lineHeight="5"
              letterSpacing="0%"
              verticalAlign="middle"
              color={cardType === 'muted' ? 'fg.muted' : 'fg.inverse-muted'}
            >
              {subTitle}
            </Text>
          ) : null}
        </HStack>
        <HStack gap="1" flexShrink={0}>
          {viewInListPath !== undefined && (
            <Tooltip
              hasArrow
              label={t`View table`}
              aria-label={t`View table`}
              placement="top-start"
            >
              <IconButton
                aria-label={t`View table`}
                size="2xs"
                icon={<Icon as={SquareArrowOutUpRight} />}
                variant="tertiary"
                color={cardType === 'muted' ? 'fg.subtle' : 'bg.subtle'}
                backgroundColor={cardType === 'muted' ? 'bg.surface' : 'fg.subtle'}
                _hover={{
                  color: cardType === 'muted' ? 'bg.subtle' : 'fg.subtle',
                  backgroundColor: cardType === 'muted' ? 'fg.subtle' : 'bg.surface',
                }}
              />
            </Tooltip>
          )}
          {openPanel !== undefined && (
            <Tooltip
              hasArrow
              label={t`View details`}
              aria-label={t`View details`}
              placement="top-start"
            >
              <IconButton
                aria-label={t`View details`}
                size="2xs"
                icon={<Icon as={ChevronRight} />}
                variant="tertiary"
                color={cardType === 'muted' ? 'fg.subtle' : 'bg.subtle'}
                backgroundColor={cardType === 'muted' ? 'bg.surface' : 'fg.subtle'}
                _hover={{
                  color: cardType === 'muted' ? 'bg.subtle' : 'fg.subtle',
                  backgroundColor: cardType === 'muted' ? 'fg.subtle' : 'bg.surface',
                }}
                onClick={openPanel}
              />
            </Tooltip>
          )}
        </HStack>
      </HStack>
      <Box w="full" h="full" ref={childrenRef} cursor="auto">
        {children}
      </Box>
    </VStack>
  );
}
